<template>
    <v-container class="pa-0">
        <div class="admin-toolbar">
            <select-option-students icon-only-mobile :custom-options="options" :value="option"
                @change="onChangeOption" />

            <div class="toolbar-search">
                <kimsa-text-field class="search-field" @change="onSearch" :value="search"
                    :placeholder="$t('common.search')" prepend-inner-icon="mdi-magnify" dense />
                <button-tooltip v-if="isStudentOption" top :tooltip="$t('common.filter')">
                    <template v-slot:activator="{ on, attrs }">
                        <button type="button" class="filter-btn" :class="{ active: !!filterCoach }" v-bind="attrs"
                            v-on="on" @click="showFilterStudents">
                            <v-icon size="17">mdi-filter-outline</v-icon>
                        </button>
                    </template>
                </button-tooltip>
            </div>

            <admin-selection-bar v-if="selectionMode" :selected-count="selectedCount" @exit="exitSelection"
                @bulk:coach="showChangeCoachBulk" @bulk:unlink="showUnlinkBulk" />
        </div>

        <admin-students-table v-if="isStudentOption" :students="students" :loading="loading"
            :selection-mode="selectionMode" :per-page="hitsPerPage" :no-data-text="emptyText"
            @toggle="onToggleStudent" @click:coach="showChangeCoach" @click:unlink="showUnlinkStudent"
            @click:profile="toStudentProfile">
            <template v-slot:footer>
                <div class="table-footer">
                    <span class="results-count">
                        {{ $tc('company.students_count', totalStudents || 0, { n: totalStudents || 0 }) }}
                    </span>
                    <kimsa-pagination v-if="pageCountStudent > 1" height="auto" :pageCount="pageCountStudent"
                        @page="pageStudent = $event - 1" :total-visible="isMobile ? 4 : 7" />
                </div>
            </template>
        </admin-students-table>

        <admin-coaches-table v-else :coaches="pagedCoaches" :loading="loading" :per-page="hitsPerPage"
            :no-data-text="emptyText" @click:unlink="showUnlinkCoach" @click:profile="toCoachProfile">
            <template v-slot:footer>
                <div class="table-footer">
                    <span class="results-count">
                        {{ $tc('company.coaches_count', filteredCoaches.length, { n: filteredCoaches.length }) }}
                    </span>
                    <kimsa-pagination v-if="pageCountCoach > 1" height="auto" :pageCount="pageCountCoach"
                        @page="pageCoach = $event - 1" :total-visible="isMobile ? 4 : 7" />
                </div>
            </template>
        </admin-coaches-table>

        <invite-student-dialog :coaches="coaches" ref="invite" />
        <student-invitations-dialog :coaches="coaches" ref="invitations" />
        <coach-invitations-dialog ref="coachInvitations" />
        <invite-coach-dialog ref="inviteCoach" />
        <student-change-coach-dialog ref="studentChangeCoachDialog" :current-coach="coach" :current-student="student"
            :coaches="coaches" @changed="onChangedStudent" :student-multiple="isBulkStudent" />
        <filter-student-by-coach-dialog :coaches="coaches" :selected="filterCoach" ref="filter"
            @filter="onFilterStudents" />
        <kimsa-confirm ref="unlink" :title="unlinkTitle" :content="unlinkContent" :loading="loadingUnlink"
            :persistent="false" @confirm="onConfirmUnlink" @cancel="onCancelUnlink" :close-on-confirm="false"
            alert-mode />

        <company-admin-panel v-if="isMobile" />
    </v-container>
</template>

<script>
import { mapMutations } from "vuex";
import { UserIndex } from "@/utils/Algolia";
import { db } from "@/utils/Firebase";
import { EventBus } from "@/utils/EventBus";
import { debounce, getInitials, getAvatarGradient } from "@/utils/Common";
import SelectOptionStudents from "@/components/coach/SelectOptionStudents";
import KimsaTextField from "@/components/common/inputs/KimsaTextField";
import KimsaPagination from "@/components/common/KimsaPagination";
import ButtonTooltip from "@/components/common/ButtonTooltip";
import KimsaConfirm from "@/components/common/KimsaConfirm";
import AdminSelectionBar from "@/components/company/admin/AdminSelectionBar";
import AdminStudentsTable from "@/components/company/admin/AdminStudentsTable";
import AdminCoachesTable from "@/components/company/admin/AdminCoachesTable";
import InviteStudentDialog from "@/components/coach/InviteStudentDialog";
import StudentInvitationsDialog from "@/components/coach/StudentInvitationsDialog";
import CoachInvitationsDialog from "@/components/company/CoachInvitationsDialog";
import InviteCoachDialog from "@/components/company/InviteCoachDialog";
import StudentChangeCoachDialog from "@/components/company/StudentChangeCoachDialog";
import FilterStudentByCoachDialog from "@/components/company/FilterStudentByCoachDialog";
import CompanyAdminPanel from "@/components/company/CompanyAdminPanel";

const PER_PAGE = 10

export default {
    name: "CompanyAdmin",
    components: {
        SelectOptionStudents,
        KimsaTextField,
        KimsaPagination,
        ButtonTooltip,
        KimsaConfirm,
        AdminSelectionBar,
        AdminStudentsTable,
        AdminCoachesTable,
        InviteStudentDialog,
        StudentInvitationsDialog,
        CoachInvitationsDialog,
        InviteCoachDialog,
        StudentChangeCoachDialog,
        FilterStudentByCoachDialog,
        CompanyAdminPanel,
    },
    data(vm = this) {
        return {
            options: [
                { name: vm.$t('common.students'), value: 'students', icon: 'mdi-account' },
                { name: vm.$t('common.coaches'), value: 'coaches', icon: 'mdi-account-multiple' },
            ],
            option: 'students',
            loading: false,
            loadingUnlink: false,
            students: [],
            student: null,
            coaches: [],
            coach: null,
            search: '',
            pageStudent: 0,
            pageCountStudent: 0,
            pageCoach: 0,
            totalStudents: null,
            hitsPerPage: PER_PAGE,
            selectionMode: false,
            filterCoach: null,
            bulkUnlink: false,
        }
    },
    computed: {
        companyId(vm = this) {
            return vm.currentUser?.uid || null
        },
        isStudentOption(vm = this) {
            return vm.option === 'students'
        },
        selectedStudents(vm = this) {
            return vm.students.filter(student => student.selected)
        },
        selectedCount(vm = this) {
            return vm.selectedStudents.length
        },
        isBulkStudent(vm = this) {
            return Array.isArray(vm.student)
        },
        filteredCoaches(vm = this) {
            const term = vm.search.trim().toLowerCase()
            if (!term) return vm.coaches
            return vm.coaches.filter(coach => `${coach.name} ${coach.email}`.toLowerCase().includes(term))
        },
        pageCountCoach(vm = this) {
            return Math.ceil(vm.filteredCoaches.length / vm.hitsPerPage) || 0
        },
        pagedCoaches(vm = this) {
            const start = vm.pageCoach * vm.hitsPerPage
            return vm.filteredCoaches.slice(start, start + vm.hitsPerPage)
        },
        emptyText(vm = this) {
            if (vm.search) return vm.$t('company.no_results_for', { query: vm.search })
            if (vm.isStudentOption && vm.filterCoach) return vm.$t('company.no_students_for_coach')
            return vm.isStudentOption ? vm.$t('company.without_students') : vm.$t('company.no_coaches')
        },
        unlinkTitle(vm = this) {
            if (vm.bulkUnlink) return vm.$t('company.unlink_bulk_title')
            return vm.$t(`common.unlink_${vm.option}_title`)
        },
        unlinkContent(vm = this) {
            if (vm.bulkUnlink) return vm.$t('company.unlink_bulk_body', { n: vm.selectedCount })
            const name = vm.isStudentOption
                ? (vm.student?.name || vm.student?.email || '')
                : (vm.coach?.name || vm.coach?.email || '')
            return vm.$t(`common.unlink_${vm.option}_subtitle`, { name })
        },
    },
    created() {
        this.restoreFromQuery()
    },
    async mounted() {
        const vm = this
        EventBus.$on('admin:invite', vm.showInviteDialog)
        EventBus.$on('admin:invitations', vm.showInvitationsDialog)
        EventBus.$on('admin:coach_invitations', vm.showCoachInvitationsDialog)
        EventBus.$on('admin:invite_coach', vm.showInviteCoachDialog)
        EventBus.$on('admin:selection', vm.startSelection)
        await vm.loadCoaches()
        await vm.loadStudents()
    },
    beforeDestroy() {
        const vm = this
        EventBus.$off('admin:invite', vm.showInviteDialog)
        EventBus.$off('admin:invitations', vm.showInvitationsDialog)
        EventBus.$off('admin:coach_invitations', vm.showCoachInvitationsDialog)
        EventBus.$off('admin:invite_coach', vm.showInviteCoachDialog)
        EventBus.$off('admin:selection', vm.startSelection)
        vm.setToolbarSubtitle('')
    },
    methods: {
        ...mapMutations(['setToolbarSubtitle']),
        async loadCoaches() {
            const vm = this
            if (!vm.companyId) return
            vm.loading = true
            try {
                const { hits } = await UserIndex.search('', {
                    filters: `coachSubscription.companyId: ${vm.companyId}`,
                    hitsPerPage: 1000,
                })
                vm.coaches = hits.map(hit => ({
                    ...hit,
                    id: hit.objectID,
                    name: hit.name || hit.email || vm._noAvailableName,
                    email: hit.email || '',
                    avatar: hit.avatar || null,
                    initials: getInitials(hit.name || hit.email),
                    avatarGradient: getAvatarGradient(hit.objectID || hit.name),
                    activeStudents: hit.activeStudents || 0,
                }))
            } catch (error) {
                vm.toast(vm.$t('common.error_generic'), 'error')
            } finally {
                vm.loading = false
            }
        },
        async loadStudents() {
            const vm = this
            let coachIds = vm.coaches.map(coach => coach.id)
            if (vm.filterCoach) coachIds = [vm.filterCoach]

            if (coachIds.length === 0) {
                vm.students = []
                vm.pageCountStudent = 0
                vm.totalStudents = 0
                return
            }

            vm.loading = true
            const byCoach = coachIds.map(id => `studentSubscription.coachId:${id}`).join(' OR ')

            try {
                const response = await UserIndex.search(vm.search, {
                    filters: `(${byCoach}) AND studentSubscription.paused != 1`,
                    hitsPerPage: vm.hitsPerPage,
                    page: vm.pageStudent,
                })

                vm.students = response.hits.map(hit => ({
                    ...hit,
                    id: hit.objectID,
                    name: hit.name || hit.email || vm._noAvailableName,
                    email: hit.email || '',
                    avatar: hit.avatar || null,
                    initials: getInitials(hit.name || hit.email),
                    avatarGradient: getAvatarGradient(hit.objectID || hit.name),
                    coachId: hit.studentSubscription?.coachId || null,
                    coachName: vm.coaches.find(coach => coach.id === hit.studentSubscription?.coachId)?.name
                        || vm._noAvailableName,
                    selected: false,
                }))
                vm.pageCountStudent = response.nbPages || 0
                vm.totalStudents = response.nbHits || 0
                vm.updateToolbarMeta()
            } catch (error) {
                vm.toast(vm.$t('common.error_generic'), 'error')
            } finally {
                vm.loading = false
            }
        },
        updateToolbarMeta() {
            const vm = this
            if (typeof vm.totalStudents !== 'number') return
            const students = vm.$tc('company.students_count', vm.totalStudents, { n: vm.totalStudents })
            const coaches = vm.$tc('company.coaches_count', vm.coaches.length, { n: vm.coaches.length })
            vm.setToolbarSubtitle(`${students} · ${coaches}`)
        },
        restoreFromQuery() {
            const vm = this
            const { tab, coachId } = vm.$route.query || {}
            if (tab === 'coaches' || tab === 'students') vm.option = tab
            if (!coachId) return

            vm.filterCoach = coachId
            const query = { ...vm.$route.query }
            delete query.coachId
            vm.$nextTick(() => vm.$router.replace({ query }).catch(() => { }))
        },
        syncQuery() {
            const vm = this
            if (vm.$route.query.tab === vm.option) return
            vm.$router.replace({ query: { ...vm.$route.query, tab: vm.option } }).catch(() => { })
        },
        onChangeOption(option) {
            const vm = this
            vm.option = option
            vm.search = ''
            vm.pageStudent = 0
            vm.pageCoach = 0
            vm.exitSelection()
            vm.syncQuery()
        },
        onSearch(value) {
            const vm = this
            vm.search = value
            vm.pageCoach = 0
            if (!vm.isStudentOption) return
            if (vm.pageStudent === 0) vm.debouncedLoadStudents()
            else vm.pageStudent = 0
        },
        debouncedLoadStudents: debounce(function () {
            this.loadStudents()
        }, 500),
        startSelection() {
            const vm = this
            if (!vm.isStudentOption) return
            vm.selectionMode = true
        },
        exitSelection() {
            const vm = this
            vm.selectionMode = false
            vm.students.forEach(student => { student.selected = false })
        },
        onToggleStudent(student) {
            student.selected = !student.selected
        },
        showInviteDialog() {
            this.$refs.invite.show()
        },
        showInvitationsDialog() {
            this.$refs.invitations.show()
        },
        showCoachInvitationsDialog() {
            this.$refs.coachInvitations.show()
        },
        showInviteCoachDialog() {
            this.$refs.inviteCoach.show()
        },
        showFilterStudents() {
            this.$refs.filter.show()
        },
        onFilterStudents(filter = null) {
            const vm = this
            vm.filterCoach = filter
            vm.pageStudent = 0
            vm.loadStudents()
        },
        showChangeCoach(student) {
            const vm = this
            vm.student = student
            vm.coach = vm.coaches.find(coach => coach.id === student.coachId) || null
            vm.$refs.studentChangeCoachDialog.show()
        },
        showChangeCoachBulk() {
            const vm = this
            if (vm.selectedCount === 0) return vm.toast(vm.$t('common.need_select_student'), 'error')
            vm.student = [...vm.selectedStudents]
            vm.coach = null
            vm.$refs.studentChangeCoachDialog.show()
        },
        showUnlinkStudent(student) {
            const vm = this
            vm.bulkUnlink = false
            vm.student = student
            vm.$refs.unlink.show()
        },
        showUnlinkBulk() {
            const vm = this
            if (vm.selectedCount === 0) return vm.toast(vm.$t('common.need_select_student'), 'error')
            vm.bulkUnlink = true
            vm.$refs.unlink.show()
        },
        showUnlinkCoach(coach) {
            const vm = this
            vm.bulkUnlink = false
            vm.coach = coach
            vm.$refs.unlink.show()
        },
        onCancelUnlink() {
            const vm = this
            vm.student = null
            vm.coach = null
            vm.bulkUnlink = false
        },
        onConfirmUnlink() {
            const vm = this
            if (vm.bulkUnlink) return vm.onUnlinkStudentsBulk()
            if (vm.isStudentOption) return vm.onUnlinkStudent()
            return vm.onUnlinkCoach()
        },
        async unlinkStudent(student) {
            const vm = this
            const subscriptionId = student?.studentSubscription?.id || null
            if (subscriptionId) await db.collection('subscriptions').doc(subscriptionId).delete()

            const payload = { studentSubscription: null }

            if (student?.group) {
                payload.group = null
                const groupRef = await db.collection('groups').doc(student.group).get()
                const students = (groupRef.data()?.students || []).filter(id => id !== student.id)
                await db.collection('groups').doc(student.group).update({ students })
            }

            if (student?.team?.id) {
                payload.team = null
                const teamRef = await db.collection('teams').doc(student.team.id).get()
                const groups = JSON.parse(JSON.stringify(teamRef.data()?.groups || []))
                const index = groups.findIndex(group => group.id === student.team.subgroupId)
                if (index >= 0) {
                    groups[index].students = (groups[index].students || []).filter(id => id !== student.id)
                    await db.collection('teams').doc(student.team.id).update({ groups })
                }
            }

            await db.collection('users').doc(student.id).update(payload)
        },
        async onUnlinkStudent() {
            const vm = this
            vm.loadingUnlink = true
            try {
                await vm.unlinkStudent(vm.student)
                vm.toast(`${vm.$t('common.student_unlinked')} ${vm.$t('common.will_be_updated_in_minutes')}`)
                vm.$refs.unlink?.hide()
                vm.student = null
                await vm.loadStudents()
            } catch (error) {
                vm.toast(vm.$t('common.error_generic'), 'error')
            } finally {
                vm.loadingUnlink = false
            }
        },
        async onUnlinkStudentsBulk() {
            const vm = this
            vm.loadingUnlink = true
            try {
                for (const student of vm.selectedStudents) {
                    // eslint-disable-next-line no-await-in-loop
                    await vm.unlinkStudent(student)
                }
                vm.toast(`${vm.$t('common.student_unlinked')} ${vm.$t('common.will_be_updated_in_minutes')}`)
                vm.$refs.unlink?.hide()
                vm.bulkUnlink = false
                vm.exitSelection()
                await vm.loadStudents()
            } catch (error) {
                vm.toast(vm.$t('common.error_generic'), 'error')
            } finally {
                vm.loadingUnlink = false
            }
        },
        async onUnlinkCoach() {
            const vm = this
            vm.loadingUnlink = true
            try {
                if (vm.coach?.coachSubscription?.id) {
                    await db.collection('subscriptions').doc(vm.coach.coachSubscription.id).delete()
                }
                await db.collection('users').doc(vm.coach.id).update({
                    accountType: 'coach',
                    coachSubscription: null,
                })
                vm.$refs.unlink?.hide()
                vm.coach = null
                await vm.loadCoaches()
                await vm.loadStudents()
            } catch (error) {
                vm.toast(vm.$t('common.error_generic'), 'error')
            } finally {
                vm.loadingUnlink = false
            }
        },
        onChangedStudent() {
            const vm = this
            vm.exitSelection()
            vm.loadStudents()
        },
        toStudentProfile(student) {
            this.pushRoute({ name: 'company.students.profile', params: { uid: student.id } })
        },
        toCoachProfile(coach) {
            this.pushRoute({ name: 'company.coaches.profile', params: { uid: coach.id } })
        },
    },
    watch: {
        pageStudent() {
            this.loadStudents()
        },
    },
}
</script>

<style scoped>
.admin-toolbar {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
    margin-bottom: 16px;
}

.toolbar-search {
    display: flex;
    align-items: center;
    gap: 8px;
    flex: 1 1 200px;
    min-width: 0;
}

.search-field {
    flex: 1 1 auto;
    max-width: 280px;
}

.filter-btn {
    width: 34px;
    height: 34px;
    border-radius: 9px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    background: var(--v-surface2-base);
    border: 1px solid var(--v-outline-base);
    color: var(--v-txtSecondary-base);
    transition: background 0.15s, color 0.15s, border-color 0.15s;
}

.filter-btn:hover {
    color: var(--v-txtPrimary-base);
}

.filter-btn.active {
    background: rgba(51, 153, 153, 0.12);
    border-color: var(--v-accent-base);
    color: var(--v-accent-base);
}

.table-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 10px 14px 4px;
    flex-wrap: wrap;
}

.results-count {
    font-size: 12px;
    color: var(--v-txtMuted-base);
}

@media (max-width: 600px) {
    .admin-toolbar {
        gap: 8px;
    }

    .search-field {
        max-width: none;
    }
}
</style>
